import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ThemedText } from '@/components/themed-text';
import api from '@/services/api';
import { getApiErrorMessage } from '@/utils/api-error';
import { useAuthStore } from '@/store/authStore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BillingAddressElement,
  CheckoutProvider,
  PaymentElement,
  useCheckout,
} from '@stripe/react-stripe-js/checkout';
import { loadStripe } from '@stripe/stripe-js';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, useWindowDimensions, View } from 'react-native';
import { trackInitiateCheckout } from '@/lib/meta-pixel';
import { useTeamPricing } from '@/hooks/use-team-pricing';
import { useTerms } from '@/hooks/use-terms';
import {
  DevTestCardButton,
  isDevTestCardEnabled,
} from '@/components/checkout/dev-test-card-button';
import { quoteTeamPlan } from '@/lib/team-pricing';
import { elementsAppearance } from '@/components/checkout/elements-appearance';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';

const publishableKey = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(publishableKey || 'pk_test_placeholder', { locale: 'pt-BR' });

const PAYMENT_FALLBACK_ERROR = 'Não foi possível concluir o pagamento. Tente novamente.';

/**
 * Só erro de cartão/validação é escrito para o pagador (e vem em pt-BR);
 * o resto é detalhe de integração e nunca aparece cru na tela.
 */
function paymentErrorMessage(
  error: { type?: string; code?: string; message?: string } | null | undefined,
) {
  if (__DEV__) console.warn('[checkout] erro do Stripe', error);
  if (error?.type === 'card_error' || error?.type === 'validation_error') {
    return error.message || PAYMENT_FALLBACK_ERROR;
  }
  return PAYMENT_FALLBACK_ERROR;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function CardPaymentForm({
  fallbackAmount,
  fallbackCurrency,
  onSuccess,
}: {
  fallbackAmount: number;
  fallbackCurrency: string;
  onSuccess: () => void;
}) {
  const checkoutState = useCheckout();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchProfile } = useAuthStore();
  const queryClient = useQueryClient();

  const submit = useCallback(
    async (paymentMethod?: string) => {
      if (checkoutState.type !== 'success') return;
      setLoading(true);
      setError(null);
      try {
        const result = await checkoutState.checkout.confirm(
          paymentMethod ? { paymentMethod } : undefined,
        );
        if (result.type === 'error') {
          setError(paymentErrorMessage(result.error as any));
          setLoading(false);
          return;
        }
        await fetchProfile();
        queryClient.invalidateQueries({ queryKey: ['provider'] });
        onSuccess();
      } catch (err: any) {
        setError(paymentErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [checkoutState, fetchProfile, queryClient, onSuccess],
  );

  // `pm_card_visa` só existe no lado do servidor: no navegador o cartão de
  // teste vira PaymentMethod a partir do token `tok_visa`.
  const payWithTestCard = useCallback(async () => {
    const stripe = await stripePromise;
    const created = await stripe?.createPaymentMethod({
      type: 'card',
      card: { token: 'tok_visa' },
      // Conta só com telefone: o Checkout exige email no cartão ou no customer.
      // Endereço: a sessão exige endereço de cobrança.
      billing_details: {
        email: 'teste@controlapag.dev',
        name: 'Cartão de teste',
        address: {
          line1: 'Av. Paulista, 1000',
          city: 'São Paulo',
          state: 'SP',
          postal_code: '01310-100',
          country: 'BR',
        },
      },
    });
    if (!created?.paymentMethod) {
      setError(paymentErrorMessage(created?.error));
      return;
    }
    await submit(created.paymentMethod.id);
  }, [submit]);

  if (checkoutState.type === 'loading') {
    return (
      <View className="items-center py-12">
        <ActivityIndicator size="large" color="#FD5001" />
      </View>
    );
  }

  if (checkoutState.type === 'error') {
    return (
      <View className="rounded-lg border border-red-200 bg-red-50 p-4">
        <ThemedText className="text-center text-red-600">
          {paymentErrorMessage(checkoutState.error as any)}
        </ThemedText>
      </View>
    );
  }

  return (
    <View className="gap-4">
      {/* Nome e endereço do titular: a sessão exige (`billing_address_collection`),
          e sem eles o antifraude barra mais cartão bom de cliente novo. */}
      <View className="w-full overflow-hidden">
        <BillingAddressElement />
      </View>
      <View className="w-full overflow-hidden" style={{ minHeight: 200 }}>
        <PaymentElement options={{ layout: 'tabs', paymentMethodOrder: ['card'] }} />
      </View>
      {error && (
        <View className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <ThemedText className="text-sm text-red-600">{error}</ThemedText>
        </View>
      )}
      {isDevTestCardEnabled(publishableKey) && (
        <DevTestCardButton busy={loading} onPress={payWithTestCard} />
      )}
      <Button onPress={() => submit()} disabled={loading}>
        {loading ? (
          <ActivityIndicator size="small" color="white" />
        ) : (
          <ThemedText className="font-semibold text-white">Confirmar pagamento</ThemedText>
        )}
      </Button>
    </View>
  );
}

export default function CheckoutScreen() {
  const colorScheme = useColorScheme();
  const params = useLocalSearchParams<{
    planId?: string;
    priceId?: string;
    amount?: string;
    /** Plano de equipe: quantidades no lugar do Price (a API recalcula o total). */
    team?: string;
    professionals?: string;
    clients?: string;
  }>();
  const { planId, priceId, amount: amountParam } = params;
  const terms = useTerms();
  const isTeam = params.team === '1';
  const teamProfessionals = Number(params.professionals ?? 0);
  const teamClients = Number(params.clients ?? 0);
  const hasTeamSelection = isTeam && teamProfessionals > 0 && teamClients > 0;
  const { data: teamPricing } = useTeamPricing({ enabled: isTeam });
  const teamQuote =
    teamPricing && hasTeamSelection
      ? quoteTeamPlan(teamPricing.version, {
          professionals: teamProfessionals,
          clients: teamClients,
        })
      : null;

  const [sessionData, setSessionData] = useState<{
    clientSecret: string;
    amountTotal: number;
    currency: string;
  } | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const initCheckoutFiredRef = React.useRef(false);

  // Conta criada só por telefone: o Stripe exige email, que vira o do perfil.
  const userEmail = useAuthStore((state) => state.user?.email);
  const fetchProfile = useAuthStore((state) => state.fetchProfile);
  const [emailDraft, setEmailDraft] = useState('');
  const [billingEmail, setBillingEmail] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const needsEmail = !userEmail && !billingEmail;

  const confirmEmail = useCallback(() => {
    const value = emailDraft.trim();
    if (!EMAIL_PATTERN.test(value)) {
      setEmailError('Digite um email válido.');
      return;
    }
    setEmailError(null);
    setSessionError(null);
    setBillingEmail(value);
  }, [emailDraft]);

  const { data: plans } = useQuery({
    queryKey: ['stripe-plans'],
    queryFn: async () => {
      const res = await api.get('/stripe/plans');
      return res.data;
    },
  });

  const plan = plans?.find((p: { id: string }) => p.id === planId);
  const amount = teamQuote
    ? teamQuote.totalCents / 100
    : (plan?.priceMonthly ?? (amountParam ? parseFloat(amountParam) : 0));
  /** A API aplicou a oferta de boas-vindas: o 1º mês sai menor que o plano. */
  const introAmount =
    sessionData && !isTeam && sessionData.amountTotal < amount ? sessionData.amountTotal : null;
  const amountDisplay = amount.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  useEffect(() => {
    if (sessionData || needsEmail) return;
    if (isTeam ? !hasTeamSelection : !planId || !priceId) return;
    const email = billingEmail ?? undefined;
    setSessionLoading(true);
    const request = isTeam
      ? api.post('/stripe/team/checkout-session', {
          professionals: teamProfessionals,
          clients: teamClients,
          email,
        })
      : api.post('/stripe/create-checkout-session', { priceId, email });
    request
      .then((res) => {
        setSessionData({
          clientSecret: res.data.clientSecret,
          amountTotal: res.data.amountTotal ?? amount,
          currency: res.data.currency || 'BRL',
        });
        if (email) fetchProfile();
      })
      .catch((err) => {
        const message = getApiErrorMessage(err, 'Erro ao preparar o pagamento');
        if (email) {
          // Volta para o campo: o email pode estar em uso em outra conta.
          setEmailError(message);
          setBillingEmail(null);
        } else {
          setSessionError(message);
        }
      })
      .finally(() => setSessionLoading(false));
  }, [
    planId,
    priceId,
    amount,
    sessionData,
    isTeam,
    hasTeamSelection,
    teamProfessionals,
    teamClients,
    needsEmail,
    billingEmail,
    fetchProfile,
  ]);

  useEffect(() => {
    if (Platform.OS !== 'web' || initCheckoutFiredRef.current || !sessionData) return;
    initCheckoutFiredRef.current = true;
    trackInitiateCheckout({
      value: sessionData.amountTotal,
      currency: sessionData.currency || 'BRL',
    });
  }, [sessionData]);

  const handleSuccess = useCallback(() => {
    router.replace('/subscription/success');
  }, []);

  const { width } = useWindowDimensions();
  const isNarrow = width < 480;

  if (isTeam ? !hasTeamSelection : !planId || !priceId) {
    return (
      <View className="flex-1 items-center justify-center bg-background px-8">
        <ThemedText className="mb-6 text-center text-foreground/70">
          Plano não informado.
        </ThemedText>
        <Button onPress={() => router.back()}>Voltar</Button>
      </View>
    );
  }

  return (
    <View className="flex-1 bg-background">
      <KeyboardAwareScrollView
        bottomOffset={KEYBOARD_BOTTOM_OFFSET}
        className="flex-1"
        contentContainerStyle={{ paddingBottom: 48, flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View className="relative overflow-hidden px-4 pb-6 pt-6 sm:pb-8 sm:pt-12">
          <View className="absolute -left-24 -top-24 h-64 w-64 rounded-full bg-primary/5" />
          <View className="relative z-10 mx-auto max-w-xl items-center text-center">
            <ThemedText className="mb-2 font-bold text-2xl text-foreground sm:text-3xl">
              Finalize sua assinatura
            </ThemedText>
            <ThemedText className="text-base text-foreground/70">
              Escolha a forma de pagamento
            </ThemedText>
          </View>
        </View>

        <View className="mx-auto w-full max-w-xl gap-6 px-4">
          <Card className="overflow-hidden border-primary/20">
            <CardContent
              className={`py-4 ${isNarrow ? 'flex-col gap-2' : 'flex-row items-center justify-between'}`}
            >
              <View className="min-w-0 flex-1">
                <ThemedText className="font-semibold text-foreground" numberOfLines={2}>
                  {isTeam
                    ? `Equipe · ${teamProfessionals} ${terms.professionalPlural}, até ${teamClients} ${terms.clientPlural}/mês`
                    : `${plan?.name ?? planId}${plan?.capacity ? ` · ${plan.capacity}` : ''}`}
                </ThemedText>
                <ThemedText className="mt-0.5 text-sm text-foreground/60">
                  {introAmount != null
                    ? `1º mês, depois R$ ${amountDisplay}/mês`
                    : 'Assinatura mensal'}
                </ThemedText>
              </View>
              <ThemedText className="shrink-0 font-bold text-xl text-primary">
                R${' '}
                {introAmount != null
                  ? introAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                  : amountDisplay}
              </ThemedText>
            </CardContent>
          </Card>

          <Card className="overflow-hidden border-border">
            <CardHeader className="border-b border-border">
              <ThemedText className="font-semibold text-lg text-foreground">
                Forma de pagamento
              </ThemedText>
            </CardHeader>
            <CardContent className="pt-5">
              {needsEmail ? (
                <View className="gap-3">
                  <View className="gap-1">
                    <ThemedText className="font-medium text-foreground">
                      Email para recibos
                    </ThemedText>
                    <ThemedText className="text-sm text-foreground/60">
                      Sua conta foi criada pelo WhatsApp. Informe um email para receber os recibos
                      da assinatura; ele também fica salvo no seu perfil.
                    </ThemedText>
                  </View>
                  <Input
                    value={emailDraft}
                    onChangeText={setEmailDraft}
                    placeholder="voce@email.com"
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect={false}
                    onSubmitEditing={confirmEmail}
                    testID="checkout-billing-email"
                  />
                  {emailError && (
                    <ThemedText className="text-sm text-red-600">{emailError}</ThemedText>
                  )}
                  <Button onPress={confirmEmail}>
                    <ThemedText className="font-semibold text-white">Continuar</ThemedText>
                  </Button>
                </View>
              ) : sessionLoading ? (
                <View className="items-center py-12">
                  <ActivityIndicator size="large" color="#FD5001" />
                  <ThemedText className="mt-4 text-foreground/70">
                    Preparando pagamento...
                  </ThemedText>
                </View>
              ) : sessionError ? (
                <View className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <ThemedText className="text-center text-red-600">{sessionError}</ThemedText>
                </View>
              ) : sessionData ? (
                <CheckoutProvider
                  stripe={stripePromise}
                  options={{
                    clientSecret: sessionData.clientSecret,
                    elementsOptions: { appearance: elementsAppearance(colorScheme) },
                  }}
                >
                  <CardPaymentForm
                    fallbackAmount={sessionData.amountTotal}
                    fallbackCurrency={sessionData.currency}
                    onSuccess={handleSuccess}
                  />
                </CheckoutProvider>
              ) : null}
            </CardContent>
          </Card>

          <Pressable
            onPress={() => router.back()}
            className="flex-row items-center justify-center gap-2 py-4"
          >
            <Ionicons className="text-neutral-500" name="arrow-back" size={18} />
            <ThemedText className="text-foreground/60">Voltar para os planos</ThemedText>
          </Pressable>
        </View>
      </KeyboardAwareScrollView>
    </View>
  );
}
